import { useState } from "react";
import { Shield, CheckCircle, AlertTriangle, XCircle, Loader2, Search, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { EmailResultsButton } from "@/components/EmailResultsButton";

interface EmailAuthCheckerProps {
  userEmail?: string | null;
  userName?: string | null;
  sessionToken?: string | null;
}

interface AuthRecord {
  exists: boolean;
  record?: string;
  status: "pass" | "warning" | "fail";
  issues: string[];
  recommendations: string[];
}

interface AuthCheckResult {
  domain: string;
  spf: AuthRecord;
  dmarc: AuthRecord;
  dkim: AuthRecord;
  overallScore: number;
  overallStatus: "pass" | "warning" | "fail";
}

export const EmailAuthChecker = ({ userEmail, userName, sessionToken }: EmailAuthCheckerProps) => {
  const [domain, setDomain] = useState("");
  const [dkimSelector, setDkimSelector] = useState("google");
  const [isChecking, setIsChecking] = useState(false);
  const [result, setResult] = useState<AuthCheckResult | null>(null);

  const commonSelectors = ["google", "selector1", "selector2", "default", "k1", "mail", "dkim"];

  const checkAuth = async () => {
    if (!domain.trim()) {
      toast.error("Please enter a domain to check");
      return;
    }

    setIsChecking(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("check-email-auth", {
        body: { domain: domain.trim(), dkimSelector },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setResult(data);
    } catch (error) {
      console.error("Error checking email auth:", error);
      toast.error("Failed to check domain. Please try again.");
    } finally {
      setIsChecking(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pass": return <CheckCircle className="w-5 h-5 text-success" />;
      case "warning": return <AlertTriangle className="w-5 h-5 text-warning" />;
      case "fail": return <XCircle className="w-5 h-5 text-destructive" />;
      default: return null;
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case "pass": return "bg-success/10 border-success/20";
      case "warning": return "bg-warning/10 border-warning/20";
      case "fail": return "bg-destructive/10 border-destructive/20";
      default: return "bg-muted";
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-success";
    if (score >= 50) return "text-warning";
    return "text-destructive";
  };

  const RecordCard = ({ title, record, description }: { title: string; record: AuthRecord; description: string }) => (
    <div className={`rounded-xl border p-4 ${getStatusBg(record.status)}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {getStatusIcon(record.status)}
          <div>
            <h4 className="font-semibold text-foreground">{title}</h4>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium uppercase ${
          record.status === "pass" ? "bg-success/20 text-success" :
          record.status === "warning" ? "bg-warning/20 text-warning" :
          "bg-destructive/20 text-destructive"
        }`}>
          {record.exists ? record.status : "Missing"}
        </span>
      </div>

      {record.record && (
        <div className="bg-background/50 rounded-lg p-2 mb-3 overflow-x-auto">
          <code className="text-xs text-foreground break-all">{record.record}</code>
        </div>
      )}

      {record.issues.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-foreground mb-1">Issues:</p>
          <ul className="space-y-1">
            {record.issues.map((issue, i) => (
              <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                <XCircle className="w-3 h-3 text-destructive shrink-0 mt-0.5" />
                {issue}
              </li>
            ))}
          </ul>
        </div>
      )}

      {record.recommendations.length > 0 && (
        <div>
          <p className="text-xs font-medium text-foreground mb-1">Recommendations:</p>
          <ul className="space-y-1">
            {record.recommendations.map((rec, i) => (
              <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                <Info className="w-3 h-3 text-primary shrink-0 mt-0.5" />
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <div className="section-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Shield className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Email Authentication Checker
          </h3>
          <p className="text-sm text-muted-foreground">
            Verify SPF, DKIM, and DMARC configuration for any domain
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="text-sm font-medium text-foreground mb-1.5 block">
              Domain
            </label>
            <Input
              value={domain}
              onChange={(e) => {
                setDomain(e.target.value);
                setResult(null);
              }}
              placeholder="example.com"
              onKeyDown={(e) => e.key === "Enter" && checkAuth()}
            />
          </div>
          <div className="sm:w-40">
            <label className="text-sm font-medium text-foreground mb-1.5 block">
              DKIM Selector
            </label>
            <select
              value={dkimSelector}
              onChange={(e) => setDkimSelector(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              {commonSelectors.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        <Button
          onClick={checkAuth}
          disabled={isChecking || !domain.trim()}
          className="w-full"
        >
          {isChecking ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Checking DNS Records...
            </>
          ) : (
            <>
              <Search className="w-4 h-4 mr-2" />
              Check Email Authentication
            </>
          )}
        </Button>

        {result && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Email Results Button */}
            <div className="flex justify-end">
              <EmailResultsButton
                toolId="auth-checker"
                toolName="Email Authentication Checker"
                results={result}
                userEmail={userEmail || null}
                userName={userName || null}
                sessionToken={sessionToken || null}
              />
            </div>

            {/* Overall Score */}
            <div className={`rounded-xl border p-4 ${getStatusBg(result.overallStatus)}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Authentication Score for <span className="font-medium text-foreground">{result.domain}</span>
                  </p>
                  <p className={`text-3xl font-bold ${getScoreColor(result.overallScore)}`}>
                    {result.overallScore}/100
                  </p>
                </div>
                <div className="text-right">
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${
                    result.overallStatus === "pass" ? "bg-success/20 text-success" :
                    result.overallStatus === "warning" ? "bg-warning/20 text-warning" :
                    "bg-destructive/20 text-destructive"
                  }`}>
                    {getStatusIcon(result.overallStatus)}
                    <span className="font-medium text-sm uppercase">{result.overallStatus}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Individual Records */}
            <div className="grid gap-4">
              <RecordCard 
                title="SPF Record" 
                record={result.spf}
                description="Specifies which servers can send email for your domain"
              />
              <RecordCard 
                title="DMARC Record" 
                record={result.dmarc}
                description="Tells receivers how to handle failed authentication"
              />
              <RecordCard 
                title="DKIM Record" 
                record={result.dkim}
                description="Cryptographic signature to verify email authenticity"
              />
            </div>

            {/* Quick Tips */}
            <div className="bg-muted/50 rounded-xl p-4">
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <Info className="w-4 h-4 text-primary" />
                Quick Tips
              </h4>
              <ul className="space-y-1 text-xs text-muted-foreground">
                <li>• A score of 80+ indicates good email authentication</li>
                <li>• SPF and DMARC are essential for deliverability</li>
                <li>• DKIM selectors vary by email provider (try different ones if not found)</li>
                <li>• Changes to DNS records can take up to 48 hours to propagate</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
